import React from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormHelperText,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PictureAsPdfIcon,
  AddOutlined,
} from "@mui/icons-material";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";
import ChangeCircleOutlinedIcon from "@mui/icons-material/ChangeCircleOutlined";
import { useTranslation } from "react-i18next";
import config from "../../config";
import { useDocumentManager } from "../../hooks/useDocumentManager";
import Upload_Icon from "../../images/managedata/uploaddoc.svg";
import { useNavigate } from "react-router";
import { ReactComponent as ArrowDownIcon } from "../../images/table/Arrow_down.svg";

export function VehicleDocumentManager({
  vehicleId,
  documents: propDocuments,
  setDocuments: setPropDocuments,
  isEditing,
  onComplete,
  orgId,
  onSubmit,
  onCancel,
  isDetailsFormValid,
  embedded = false,
  showDeleteButton = false,
  onDelete,
  saveButtonText,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const documentTypes = [
    "RC Book",
    "Emission Test Report",
    "Insurance Policy",
    "Other",
  ];
  const specificTypes = ["RC Book", "Emission Test Report", "Insurance Policy"];
  const maxOtherDocuments = 3;

  const {
    documents,
    isLoading,
    isSubmitting,
    alert,
    errors,
    touched,
    confirmModal,
    setAlert,
    setTouched,
    canAddDocument,
    getRemainingOtherDocs,
    getAvailableDocTypes,
    handleAddDocument,
    handleRemoveDocument,
    handleCancelRemove,
    handleConfirmRemove,
    handleDocTypeChange,
    handleFileUpload,
    handleSubmitDocuments,
    handleDownload,
    handleSave,
  } = useDocumentManager({
    entityId: vehicleId,
    entityType: "Vehicle",
    documents: propDocuments,
    setDocuments: setPropDocuments,
    isEditing,
    orgId,
    documentTypes,
    specificTypes,
    maxOtherDocuments,
    apiEndpoint: "/vehicle/id",
    config,
    onSubmit,
    onComplete,
  });

  const renderFilePreview = (doc) => {
    if (!doc.preview) return null;

    const isPdf =
      doc.fileType === "application/pdf" ||
      (doc.fileName && doc.fileName.toLowerCase().endsWith(".pdf"));

    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        {isPdf ? (
          <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
            <object
              data={doc.preview}
              type="application/pdf"
              width="100%"
              height="100%"
              style={{ border: "none" }}
            >
              <Box sx={{ textAlign: "center", p: 2 }}>
                <PictureAsPdfIcon sx={{ fontSize: 48, color: "#F44336" }} />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t("PDF Preview Not Available")}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 2 }}
                  onClick={() => window.open(doc.preview, "_blank")}
                >
                  {t("View PDF")}
                </Button>
              </Box>
            </object>
          </Box>
        ) : (
          <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
            <img
              src={doc.preview}
              alt="Document preview"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                display: "block",
                margin: "0 auto",
              }}
            />
          </Box>
        )}
        {doc.isUploaded && !doc.needsReplacement && (
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              bottom: 8,
              left: 0,
              right: 0,
              textAlign: "center",
              backgroundColor: "rgba(255,255,255,0.8)",
              padding: "4px 8px",
              color: "green",
              borderRadius: "4px",
            }}
          >
            ✓ {t("Uploaded")}
          </Typography>
        )}
        {doc.needsReplacement && (
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              bottom: 8,
              left: 0,
              right: 0,
              textAlign: "center",
              backgroundColor: "rgba(255,255,255,0.8)",
              padding: "4px 8px",
              color: "orange",
              borderRadius: "4px",
            }}
          >
            {t("Pending replacement")}
          </Typography>
        )}
      </Box>
    );
  };

  const errorTextStyles = {
    color: "#d32f2f",
    fontSize: "0.75rem",
    mt: 0.5,
    ml: 1.5,
  };

  return (
    <Box
      sx={{
        padding: { xs: "20px", md: "40px" },
        margin: embedded
          ? 0
          : { xs: "0px 20px", md: "0px 40px 0px 40px" },
        backgroundColor: "#FFFFFF",
        border: "1px solid #A5DCF3",
        borderRadius: "10px",
        height: "100%",
        boxShadow: "-2px 2px 5px 0px #00000040",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          mb: 4,
        }}
      >
        <Box sx={{ mb: { xs: 2, md: 0 } }}>
          <Typography
            variant="h6"
            sx={{ mb: 3, fontWeight: "bold", color: "#000" }}
          >
            {t("Upload Documents")} ({t("RC Book")}, {t("Emission Test Report")}
            , {t("Insurance Policy")})
            {/* <Typography
              variant="body2"
              component="span"
              color="error"
              sx={{ ml: 0.5 }}
            >
              *
            </Typography> */}
          </Typography>
        </Box>
      </Box>

      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 200,
          }}
        >
          <CircularProgress size={48} />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {documents.map((doc) => (
            <Grid item xs={12} sm={4} key={doc.id}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  // border: "1px solid #183059",
                  border: errors[doc.id]?.file
                    ? "1px solid #d32f2f"
                    : "1px solid #183059",
                  borderRadius: "8px",
                  padding: 2,
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    mb: 1,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 1,
                  }}
                >
                  <FormControl
                    fullWidth
                    error={
                      !!(touched[doc.id]?.type && errors[doc.id]?.type) ||
                      (doc.preview && !doc.type)
                    }
                    // sx={{ maxWidth: "3",bgcolor:"black" }}
                  >
                    <Select
                      value={doc.type}
                      onChange={(e) =>
                        handleDocTypeChange(doc.id, e.target.value)
                      }
                      onBlur={() =>
                        setTouched((prev) => ({
                          ...prev,
                          [doc.id]: { ...prev[doc.id], type: true },
                        }))
                      }
                      disabled={
                        doc.isExisting &&
                        doc.isUploaded &&
                        !doc.needsReplacement
                      }
                      displayEmpty
                      sx={{
                        fontSize: "20px",
                        borderRadius: "8px",
                        height: "40px",
                        backgroundColor: "#B7BFCC",
                        color: "#111214",
                        "& .MuiSelect-select": {
                          padding: "8px 14px",
                          display: "flex",
                          alignItems: "center",
                        },
                        "& svg": {
                          color: "#000",
                        },
                      }}
                      IconComponent={ArrowDownIcon}
                    >
                      <MenuItem value="" disabled>
                        {t("Select a document type")}
                      </MenuItem>
                      {getAvailableDocTypes(doc.id).availableTypes.map(
                        (type) => {
                          const isTypeUsedElsewhere =
                            specificTypes.includes(type) &&
                            documents.some(
                              (d) =>
                                d.id !== doc.id &&
                                d.type === type &&
                                (d.isUploaded || d.file)
                            );

                          return (
                            <MenuItem
                              key={type}
                              value={type}
                              disabled={
                                isTypeUsedElsewhere && doc.type !== type
                              }
                            >
                              {t(type)}
                              {isTypeUsedElsewhere && doc.type !== type && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ ml: 1 }}
                                >
                                  {t("(Already uploaded)")}
                                </Typography>
                              )}
                            </MenuItem>
                          );
                        }
                      )}
                    </Select>
                    {((touched[doc.id]?.type && errors[doc.id]?.type) ||
                      (doc.preview && !doc.type)) && (
                      <FormHelperText sx={errorTextStyles}>
                        {errors[doc.id]?.type ||
                          t("Please select a document type")}
                      </FormHelperText>
                    )}
                  </FormControl>
                  {/* Add Delete button for empty boxes */}
                  {!doc.preview ? (
                    documents.length === 1 ? null : (
                      <IconButton
                        onClick={() => handleRemoveDocument(doc.id)}
                        disabled={isSubmitting}
                        sx={{
                          borderRadius: 2,
                          border: "1px solid #414958",
                          width: 40,
                          height: 40,
                          backgroundColor: "#fff",
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                          },
                        }}
                      >
                        X{/* <DeleteIcon sx={{ color: "#F44336" }} /> */}
                      </IconButton>
                    )
                  ) : (
                    <IconButton
                      onClick={() => handleDownload(doc)}
                      disabled={isSubmitting}
                      sx={{
                        borderRadius: 2,
                        border: "1px solid #ccc",
                        width: 40,
                        height: 40,
                        backgroundColor: "#fff",
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                        },
                      }}
                    >
                      <ArrowDownwardOutlinedIcon sx={{ color: "#1976D2" }} />
                    </IconButton>
                  )}
                </Box>

                <Box
                  sx={{
                    width: "100%",
                    height: "300px",
                    // border: errors[doc.id]?.file
                    //   ? "1px dashed #d32f2f"
                    //   : "1px solid #183059",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    mb: 1,
                  }}
                >
                  {doc.preview ? (
                    renderFilePreview(doc)
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        p: 3,
                        textAlign: "center",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 2,
                        }}
                      >
                        <AddOutlined sx={{ fontSize: 32 }} />
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {t("New Document")}
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        sx={{
                          borderRadius: "8px",
                          textTransform: "none",
                          backgroundColor: "#20A8E0",
                          "&:hover": { backgroundColor: "#1565C0" },
                          py: 1,
                          px: 3,
                          color: "#fff",
                          fontWeight: "bold",
                          fontSize: "14px",
                        }}
                        startIcon={
                          <img src={Upload_Icon} alt="upload" />
                          // <ChangeCircleOutlinedIcon sx={{ fontSize: "16px" }} />
                        }
                        onClick={() =>
                          document
                            .getElementById(`file-upload-${doc.id}`)
                            .click()
                        }
                      >
                        {t("Upload")}
                      </Button>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 2 }}
                      >
                        {t("Supported formats: JPG, JPEG, PNG, PDF (Max 10MB)")}
                      </Typography>
                      <input
                        id={`file-upload-${doc.id}`}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,application/pdf"
                        hidden
                        onChange={(e) => handleFileUpload(doc.id, e)}
                      />
                    </Box>
                  )}
                </Box>
                {doc.preview && (
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 1,
                      mb: 1,
                      mt: 1,
                    }}
                  >
                    <Button
                      variant="contained"
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        backgroundColor: "#20A8E0",
                        "&:hover": { backgroundColor: "#1565C0" },
                        py: 1.5,
                        px: 3,
                        color: "#fff",
                        fontWeight: "bold",
                        fontSize: "16px",
                        minWidth: "120px",
                      }}
                      startIcon={
                        <ChangeCircleOutlinedIcon sx={{ fontSize: "16px" }} />
                      }
                      component="label"
                      disabled={isSubmitting}
                    >
                      {t("Change")}
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,application/pdf"
                        hidden
                        key={doc.id}
                        onChange={(e) => handleFileUpload(doc.id, e)}
                      />
                    </Button>
                    <Button
                      variant="contained"
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        backgroundColor: "#F44336",
                        "&:hover": { backgroundColor: "#D32F2F" },
                        py: 1.5,
                        px: 3,
                        color: "#fff",
                        fontWeight: "bold",
                        fontSize: "16px",
                        minWidth: "120px",
                      }}
                      startIcon={<DeleteIcon sx={{ fontSize: "16px" }} />}
                      onClick={() => handleRemoveDocument(doc.id)}
                      disabled={isSubmitting}
                    >
                      {t("Delete")}
                    </Button>
                  </Box>
                )}
                {(touched[doc.id]?.file && errors[doc.id]?.file) ||
                (doc.type && !doc.file && !doc.isUploaded && !doc.preview) ? (
                  <FormHelperText sx={errorTextStyles}>
                    {errors[doc.id]?.file || t("Please upload a document file")}
                  </FormHelperText>
                ) : null}
              </Box>
            </Grid>
          ))}
          {canAddDocument() && (
            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  height: 350,
                  border: "2px dashed #1976D2",
                  borderRadius: "8px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "#E3F2FD",
                  cursor: "pointer",
                }}
                onClick={handleAddDocument}
              >
                <Box sx={{ textAlign: "center" }}>
                  <AddIcon sx={{ fontSize: 48, color: "#1976D2", mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    {t("Add New Document")}
                  </Typography>
                  {getRemainingOtherDocs() > 0 && (
                    <Typography
                      variant="caption"
                      sx={{ mt: 1, color: "#1976D2" }}
                    >
                      {t(
                        `${getRemainingOtherDocs()} Other document${
                          getRemainingOtherDocs() !== 1 ? "s" : ""
                        } remaining`
                      )}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      )}

      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          gap: "20px",
          marginTop: "40px",
        }}
      >
        {showDeleteButton && (
          <Button
            onClick={onDelete}
            disabled={isSubmitting}
            sx={{
              width: 120,
              fontSize: "20px",
              color: "#F20000",
              height: "55px",
              borderRadius: "8px",
              textTransform: "none",
              padding: "8px 24px",
              background: "#FFFFFF",
              border: "1px solid #F20000",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#FFF0F0",
              },
              "&:disabled": {
                background: "#F5F5F5",
                color: "#A0A0A0",
                border: "1px solid #E0E0E0",
              },
            }}
          >
            {t("Delete")}
          </Button>
        )}
        <Button
          onClick={onCancel}
          sx={{
            width: 120,
            fontSize: "20px",
            color: "#1F242E",
            height: "55px",
            borderRadius: "8px",
            textTransform: "none",
            padding: "8px 24px",
            background: "#E0E0E0",
            border: "1px solid #414958",
            fontWeight: 600,
          }}
        >
          {t("Cancel")}
        </Button>
        <Button
          onClick={async () => {
            console.log("Save button clicked");

            try {
              // Don't call setIsSubmitting here - it's managed by the hook

              const savedVehicleId = await onSubmit();

              if (!savedVehicleId) {
                // throw new Error("Vehicle save failed");
                return;
              }

              const docsToUpload = documents.filter(
                (doc) => doc.file && !doc.isUploaded && doc.type
              );

              if (docsToUpload.length > 0) {
                await handleSubmitDocuments(savedVehicleId);

                if (embedded) {
                  setTimeout(() => onComplete?.(), 1500);
                }
              } else {
                // If no documents to upload, show success and navigate
                setAlert({
                  open: true,
                  message: t("Vehicle saved successfully"),
                  severity: "success",
                });

                setTimeout(() => {
                  if (onComplete) {
                    onComplete();
                    return;
                  }

                  navigate("/Vehicle");
                }, 1500);
              }
            } catch (error) {
              console.error("Save failed:", error);
              setAlert({
                open: true,
                message: t("Failed to save. Please try again."),
                severity: "error",
              });
            }
          }}
          disabled={isSubmitting}
          sx={{
            width: 120,
            height: "55px",
            fontSize: "20px",
            color: "#1F242E",
            textTransform: "none",
            borderRadius: "8px",
            padding: "8px 24px",
            background: "#F9E719",
            border: "1px solid #414958",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: "#f1de16ff",
            },
          }}
        >
          {isSubmitting ? `${t("Saving")}...` : saveButtonText || t("Save")}
        </Button>
      </Box>

      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
      <Dialog
        open={confirmModal.open}
        onClose={handleCancelRemove}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: "10px",
            boxShadow: "-2px 2px 5px 0px #00000040",
          },
        }}
      >
        <DialogTitle
          id="alert-dialog-title"
          sx={{ bgcolor: "#F2F2F3", color: "#414958" }}
        >
          {t("Confirm Document Removal")}
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 1 }}>
          <DialogContentText id="alert-dialog-description">
            {t(
              "Are you sure you want to remove this document? This action cannot be undone."
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCancelRemove}
            sx={{
              color: "#1F242E",
              borderRadius: "8px",
              textTransform: "none",
              padding: "8px 16px",
              background: "#F2F2F3",
              border: "1px solid #414958",
            }}
          >
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleConfirmRemove}
            sx={{
              color: "#FFFFFF",
              borderRadius: "8px",
              textTransform: "none",
              padding: "8px 16px",
              background: "#d32f2f",
              "&:hover": { background: "#b71c1c" },
            }}
            autoFocus
          >
            {t("Remove")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
